import { alpha } from '@mui/material/styles';

/**
 * Theme extensions for the Complete Review feature
 * Centralizes all styling constants and component overrides
 */

export const reviewThemeExtensions = {
  spacing: {
    xs: 0.5,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
  },
  
  layout: {
    sidebarWidth: 250,
    maxContentWidth: 1900,
    sidebarBreakpoint: 1600,
    gridMinHeight: '600px',
    panelGap: 3,
  },
  
  transitions: {
    sidebar: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    hover: 'all 0.2s',
  },
  
  components: {
    // Review sections wrapper styling
    reviewSection: (theme) => ({
      p: 3,
      backgroundColor: alpha(theme.palette.grey[100], 0.5),
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 1,
      border: `1px solid ${theme.palette.divider}`,
    }),
    
    // Question/Answer preview container
    previewContainer: {
      overflow: 'auto',
      maxWidth: '100%',
      flex: 1,
    },
    
    // Editor container
    editorContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    
    // Navigation buttons
    navigationButton: (theme) => ({
      minWidth: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'scale(1.05)',
      },
      '&:disabled': {
        opacity: 0.5,
      },
    }),
    
    // Sidebar styles
    sidebar: (theme) => ({
      width: 250,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: 250,
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        borderRight: `1px solid ${theme.palette.divider}`,
      },
    }),
    
    // Question list item
    questionListItem: (theme, isActive, isVerified) => ({
      cursor: 'pointer',
      backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
      borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      opacity: isVerified ? 0.7 : 1,
    }),
    
    // Topic chip
    topicChip: (theme) => ({
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[2],
      },
    }),
    
    // Verification status chip
    verificationChip: (theme, isVerified) => ({
      backgroundColor: isVerified 
        ? alpha(theme.palette.success.main, 0.1)
        : alpha(theme.palette.warning.main, 0.1),
      color: isVerified
        ? theme.palette.success.dark
        : theme.palette.warning.dark,
      fontWeight: 500,
    }),
    
    // Image container in content
    imageContainer: {
      '& img': {
        maxWidth: '400px',
        maxHeight: '300px',
        width: 'auto',
        height: 'auto',
        display: 'block',
        margin: '10px auto',
        objectFit: 'contain',
      },
    },
    
    // MCQ option card
    mcqOptionCard: (theme, isSelected) => ({
      p: 3,
      cursor: 'pointer',
      backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'white',
      border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0,0,0,0.12)',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        transform: 'translateY(-2px)',
      },
    }),
    
    // Action button base
    actionButton: (theme) => ({
      textTransform: 'none',
      fontWeight: 500,
      px: 3,
      py: 1,
    }),
    
    // Floating navigation container
    floatingNavigation: (theme) => ({
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      display: 'flex',
      gap: theme.spacing(1),
      zIndex: 10,
    }),
    
    // Validation alert
    validationAlert: (theme, severity) => ({
      mb: 2,
      '& .MuiAlert-icon': {
        fontSize: '1.5rem',
      },
    }),
  },
};

/**
 * Apply review theme extensions to a base theme
 */
export const applyReviewTheme = (baseTheme) => {
  return {
    ...baseTheme,
    review: reviewThemeExtensions,
  };
};